import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CreditCard, Loader2, AlertTriangle } from "lucide-react";
import { api, formatApiError } from "@/api/client";
import { fmtDate } from "@/utils/datetime";

function money(minor: number, currency: string, lang: string) {
    return new Intl.NumberFormat(lang, { style: "currency", currency }).format(minor / 100);
}

export default function SubscriptionTab() {
    const { t, i18n } = useTranslation();
    const [data, setData] = useState<any>(null);
    const [plans, setPlans] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState<string | null>(null);

    useEffect(() => {
        (async () => {
            try {
                const [me, pub] = await Promise.all([api.get("/institution/subscription"), api.get("/plans/public")]);
                setData(me.data); setPlans(pub.data.items || []);
            } catch (e: any) { setErr(formatApiError(e)); }
            finally { setLoading(false); }
        })();
    }, []);

    if (loading) return <div className="flex justify-center p-8"><Loader2 className="animate-spin" size={22} /></div>;
    if (err) return <div className="rounded-md p-3 text-sm" style={{ background: "var(--error-bg)", color: "var(--error-fg)" }}>{err}</div>;
    if (!data?.subscription) return <div className="text-sm" style={{ color: "var(--text-muted)" }} data-testid="sub-none">{t("sub.none")}</div>;

    const s = data.subscription;
    const usage = data.usage || {};
    const limits = s.plan?.limits || {};
    const pct = (used: number, lim: any) => (lim == null ? 0 : Math.round((used / lim) * 100));

    return (
        <div className="space-y-4 max-w-3xl" data-testid="sub-tab">
            <div className="rounded-xl border p-4" style={{ borderColor: "var(--border)", background: "var(--card)" }}>
                <div className="flex items-center gap-2 mb-2"><CreditCard size={18} /><h2 className="text-lg font-semibold">{s.plan?.name} — {t(`sub.status.${s.status}`, s.status)}</h2></div>
                <div className="text-sm" style={{ color: "var(--text-muted)" }}>{money(s.plan?.price_monthly_minor || 0, s.plan?.currency || "AZN", i18n.language)} / {t("plan.month")}</div>
                {s.status === "trial" && s.trial_ends_at && <div className="mt-2 text-sm" style={{ color: "var(--info-fg)" }}>{t("sub.trial_ends", { date: fmtDate(s.trial_ends_at) })}</div>}
                {s.status === "past_due" && <div className="mt-2 rounded-md p-2 text-sm inline-flex items-center gap-1.5" style={{ background: "var(--warning-bg)", color: "var(--warning-fg)" }} data-testid="sub-past-due"><AlertTriangle size={14} />{t("sub.past_due_warn")}</div>}
                {s.status === "suspended" && <div className="mt-2 rounded-md p-2 text-sm inline-flex items-center gap-1.5" style={{ background: "var(--error-bg)", color: "var(--error-fg)" }} data-testid="sub-suspended"><AlertTriangle size={14} />{t("sub.suspended_warn")}</div>}
            </div>

            <div className="rounded-xl border p-4 space-y-2" style={{ borderColor: "var(--border)", background: "var(--card)" }}>
                <h3 className="text-sm font-semibold">{t("sub.usage")}</h3>
                {[["branches", "max_branches"], ["users", "max_users"], ["active_students", "max_active_students"]].map(([uk, lk]) => {
                    const u = usage[uk] || 0; const l = limits[lk];
                    return (
                        <div key={uk} data-testid={`sub-usage-${uk}`}>
                            <div className="flex justify-between text-sm"><span>{t(`sub.usage.${uk}`)}</span><span>{u}{l != null ? ` / ${l}` : ` / ${t("plan.unlimited")}`}</span></div>
                            {l != null && (
                                <div className="mt-1 h-1.5 rounded-full overflow-hidden" style={{ background: "var(--input)" }}>
                                    <div className="h-full rounded-full" style={{ width: `${Math.min(100, pct(u, l))}%`, background: pct(u, l) >= 80 ? "var(--warning-fg)" : "var(--primary)" }} />
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            <div className="rounded-xl border p-4" style={{ borderColor: "var(--border)", background: "var(--card)" }}>
                <h3 className="text-sm font-semibold mb-3">{t("sub.compare")}</h3>
                <div className="grid gap-3 md:grid-cols-3">
                    {plans.map((p) => (
                        <div key={p.code} className="rounded-md border p-3 text-sm" style={{ borderColor: p.code === s.plan?.code ? "var(--primary)" : "var(--border)" }} data-testid={`sub-plan-${p.code}`}>
                            <div className="font-semibold">{p.name}</div>
                            <div className="text-xl font-bold mt-1">{money(p.price_monthly_minor, p.currency, i18n.language)}<span className="text-xs font-normal" style={{ color: "var(--text-muted)" }}>/{t("plan.month")}</span></div>
                            <div className="text-xs mt-2 space-y-0.5" style={{ color: "var(--text-muted)" }}>
                                {Object.entries(p.limits || {}).map(([k, v]) => <div key={k}>{t(`plan.limit.${k}`, k)}: {v == null ? t("plan.unlimited") : String(v)}</div>)}
                            </div>
                        </div>
                    ))}
                </div>
                <div className="mt-3 text-xs" style={{ color: "var(--text-muted)" }}>{t("sub.upgrade_cta")}</div>
            </div>
        </div>
    );
}
